import { Time } from "./time";
import { Model } from "./model";
import { WithTime } from "./interfaces/with_time";
import { HasSocketEvent } from "@models/interfaces";
import { SocketEvent } from "@helpers/types";
import { BestFeeCollection } from "./best_fee_collection";
import { VsnFeeCollection } from "@models/vsn_fee_collection";

export interface ITradeTag {
	attributes: ITradeTagAttributes;
}

export interface ITradeTagAttributes {
	name: string;
	short_name: string;
}

export interface ITradeTax {
	amount_fiat: string;
	fiat_id: number;
	fiat_amount_after_tax: string;
}

export interface ITradeFee {
	fee_amount_in_fiat: string;
	fee_percentage: number;
}

export class Trade extends Model implements WithTime, HasSocketEvent {
	private amount_cryptocoin: string = "";
	private amount_fiat: string = "";
	private amount: string = "";
	private bitpandatogo_serial_number: string = "";
	private cryptocoin_id: string = "";
	private fiat_id: string = "";
	private fiat_wallet_id: string = "";
	private fiat_to_eur_rate: number = 1;
	private provider_id: string = "";
	private status: string = "";
	private time: Time = new Time();
	private type: string = "";
	private zahlungsreferenz: string = "";
	private payment_info: boolean = false;
	private payment_option_id: string = "";
	private requires_2fa_approval: boolean = false;
	private payLink: string = null;
	private wallet_id: string = "";
	private price: string = "";
	private inline: boolean = false;
	private is_swap: boolean = false;
	private is_savings: boolean = false;
	private is_index: boolean = false;
	private related_swap_trade_id: string = "";
	private related_swap_trade: Trade = null;
	private reward_campaign: string = "";
	private bfc_used: boolean = false;
	private best_fee_collection: BestFeeCollection = null;
	private vsn_fee_collection: VsnFeeCollection;
	private tags = [];
	private tax: Partial<ITradeTax> = {};
	private fee: Partial<ITradeFee> = {};
	private is_fee_transparent = false;
	starting_borrow: string = "";
	closing_borrow: string = "";

	// Backend/team-php/services/api/-/issues/1560
	// This is only set if the trade is related to stocks or etfs
	private premium: number = 0;

	// Socket usage:
	public eventData: SocketEvent = "";

	public isStatus(s: string): boolean {
		return this.status === s;
	}

	public getTax(): Partial<ITradeTax> {
		return this.tax;
	}

	public getFee(): Partial<ITradeFee> {
		return this.fee;
	}
	public getIsFeeTransparent(): boolean {
		return this.is_fee_transparent;
	}

	public getProviderID(): string {
		return this.provider_id;
	}

	public setBestFeeCollectionEntity(bfc: BestFeeCollection): void {
		this.best_fee_collection = bfc;
	}

	public getCurrencyId(): string {
		return this.cryptocoin_id;
	}

	public getAmountTradable(): string {
		return this.amount_cryptocoin;
	}

	public getAmountFiat(): string {
		return this.amount_fiat;
	}

	public getBitpandatogoSerialNumber(): string {
		return this.bitpandatogo_serial_number;
	}

	public getBestFeeCollection(): BestFeeCollection {
		return this.best_fee_collection;
	}

	public getVsnFeeCollection(): VsnFeeCollection {
		return this.vsn_fee_collection;
	}

	public getPayReference(): string {
		return this.zahlungsreferenz;
	}

	public getStatus(): string {
		return this.status;
	}

	public getBfcUsed(): boolean {
		return this.bfc_used;
	}

	public setStatus(status: string): void {
		this.status = status;
	}

	public getFiatToEurRate(): number {
		return this.fiat_to_eur_rate;
	}

	public setPayLink(link: string): void {
		this.payLink = link;
	}

	public requires2Fa(): boolean {
		return this.requires_2fa_approval;
	}

	public setRequires2Fa(n: boolean): void {
		this.requires_2fa_approval = n;
	}

	public getPayLink(): string {
		return this.payLink;
	}

	public getFiatId(): string {
		return this.fiat_id;
	}

	public getTime(): Time {
		return this.time;
	}

	public getWalletId(): string {
		return this.wallet_id;
	}

	public getPrice(): number {
		return +this.price;
	}

	public getFiatWalletId(): string {
		return this.fiat_wallet_id;
	}

	public getType(): string {
		return this.type;
	}

	public get isVoucher(): boolean {
		return this.getBitpandatogoSerialNumber().length > 0;
	}

	public isType(type: string): boolean {
		return type === this.type;
	}

	public hasFiatWallet(): boolean {
		return this.fiat_wallet_id !== "";
	}

	public hasCryptoWallet(): boolean {
		return this.wallet_id !== "";
	}

	public getPaymentOptionId(): string {
		return this.payment_option_id;
	}

	public hasProvider(): boolean {
		return this.payment_option_id !== "" && this.payment_option_id !== "12"; // Fiatwallet
	}

	public getInline(): boolean {
		return this.inline;
	}

	public toggle(): void {
		this.inline = !this.inline;
	}

	public setInline(val: boolean): void {
		this.inline = val;
	}

	public getIsSwap(): boolean {
		return this.is_swap;
	}

	public setIsSwap(val: boolean): void {
		this.is_swap = val;
	}

	public getIsIndex(): boolean {
		return this.is_index;
	}

	public getIsSavings(): boolean {
		return this.is_savings;
	}

	public setIsSavings(b: boolean): void {
		this.is_savings = b;
	}

	public getRelatedSwapTradeId(): string {
		return this.related_swap_trade_id;
	}

	public getRelatedSwapTrade(): Trade {
		return this.related_swap_trade;
	}

	public getAmount(): string {
		return this.amount;
	}

	public setAmount(s: string): void {
		this.amount = s;
	}

	public getRewardCampaign(): string {
		return this.reward_campaign;
	}

	public getEvent(): string {
		return this.eventData;
	}

	public getStockSpread(): number {
		return this.premium;
	}

	public getTags(): ITradeTag[] {
		return this.tags;
	}
}
